import React, { useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { TbSquareRoundedCheck, TbSquareRoundedX } from "react-icons/tb";
import MastiSkeleton from "./MastiSkeleton";
import Pagination from "Components/Pagination/Pagination";
import ModalWrapper from "Components/Modal/ModalWrapper";

const BASE_URL = process.env.REACT_APP_API_URL;

// ── Badges ───────────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    approved: "bg-green-50 text-green-600 border-green-200",
    rejected: "bg-red-50 text-red-500 border-red-200",
    pending: "bg-yellow-50 text-yellow-600 border-yellow-200",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-0.5 text-xs font-semibold ${
        map[status] || "border-gray-200 bg-gray-100 text-gray-500"
      }`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const TypeBadge = ({ type }) => (
  <span
    className={`inline-flex items-center rounded-full border px-3 py-0.5 text-xs font-semibold ${
      type === "paid"
        ? "border-purple-200 bg-purple-50 text-purple-600"
        : "border-gray-200 bg-gray-100 text-gray-500"
    }`}
  >
    {type.charAt(0).toUpperCase() + type.slice(1)}
  </span>
);

// ── Action Modal Content ──────────────────────────────────────────────────────
const ActionModal = ({ request, actionType, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const isApprove = actionType === "approved";

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await axios.put(`${BASE_URL}/masti/update-status/${request._id}`, {
        status: actionType,
      });
      onClose();
      onSuccess();
      Swal.fire({
        icon: isApprove ? "success" : "error",
        title: isApprove ? "Approved!" : "Rejected!",
        text: `${
          request.host?.nickName || "Request"
        } masti request has been ${actionType} successfully.`,
        confirmButtonColor: isApprove ? "#22c55e" : "#ef4444",
        timer: 2500,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } catch (err) {
      onClose();
      Swal.fire({
        icon: "error",
        title: "Failed",
        text:
          err?.response?.data?.message || "Something went wrong. Try again.",
        confirmButtonColor: "#c51369",
      });
    } finally {
      setLoading(false);
    }
  };

  const name = request.host?.nickName || "—";
  const phone = request.host?.phone || "—";
  const avatar =
    request.host?.selected_Avatar?.avatarUrl ||
    `https://api.dicebear.com/7.x/thumbs/svg?seed=${name}`;

  return (
    <div className="w-full px-6 py-8 sm:px-10 sm:py-10">
      {/* Icon */}
      <div
        className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${
          isApprove ? "bg-green-50" : "bg-red-50"
        }`}
      >
        {isApprove ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-7 w-7 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-7 w-7 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        )}
      </div>

      {/* Title */}
      <h2 className="text-center text-lg font-bold text-gray-800 sm:text-xl">
        {isApprove ? "Approve Request?" : "Reject Request?"}
      </h2>
      <p className="mt-1 text-center text-sm text-gray-400">
        Review the details below before confirming.
      </p>

      {/* User info card */}
      <div className="mt-5 flex flex-col gap-3 rounded-xl bg-gray-50 p-4 sm:flex-row sm:items-center sm:gap-4">
        {/* Avatar + name */}
        <div className="flex min-w-0 items-center gap-3">
          <img
            src={avatar}
            alt={name}
            className="h-11 w-11 shrink-0 rounded-full border-2 border-pink-100 object-cover"
          />
          <div className="min-w-0">
            <p className="truncate font-semibold text-gray-800">{name}</p>
            <p className="text-xs text-gray-400">+91 {phone}</p>
          </div>
        </div>

        {/* Divider on sm+ */}
        <div className="hidden h-10 w-px bg-gray-200 sm:block" />

        {/* Type + language */}
        <div className="flex items-center justify-between gap-3 sm:ml-auto sm:flex-col sm:items-end sm:justify-center">
          <TypeBadge type={request.type} />
          <span className="text-xs text-gray-400">
            {request.language || "—"}
          </span>
        </div>
      </div>

      {/* Description */}
      <p className="mt-4 text-center text-sm leading-relaxed text-gray-500">
        Are you sure you want to{" "}
        <span
          className={`font-semibold ${
            isApprove ? "text-green-600" : "text-red-500"
          }`}
        >
          {actionType}
        </span>{" "}
        this masti request?
        {!isApprove && request.type === "paid" && (
          <span className="mt-1 block text-xs text-gray-400">
            💰 Coins will be refunded to the host automatically.
          </span>
        )}
      </p>

      {/* Actions */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <button
          onClick={onClose}
          className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          disabled={loading}
          className={`flex-1 rounded-xl py-2.5 text-sm font-semibold text-white transition disabled:opacity-60 ${
            isApprove
              ? "bg-pink-600 hover:bg-pink-700"
              : "bg-red-500 hover:bg-red-600"
          }`}
        >
          {loading
            ? "Processing..."
            : isApprove
            ? "Yes, Approve"
            : "Yes, Reject"}
        </button>
      </div>
    </div>
  );
};

// ── Main Export ───────────────────────────────────────────────────────────────
const MastiTable = ({
  data,
  loading,
  error,
  currentPage,
  limit,
  totalPages,
  onPageChange,
  onActionSuccess,
  canEdit = true,
  statusFilter,
  makeMastiOffline,
}) => {
  const [popup, setPopup] = useState(null);

  const handleClose = () => setPopup(null);

  return (
    <>
      <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="bg-[#f0d9e4] text-center">
              {[
                "Apply Sl No.",
                "Name",
                "PageName",
                "Category",
                "Language",
                "Type",
                "Status / Action",
              ].map((col) => (
                <th
                  key={col}
                  className="text-black px-4 py-3 text-center text-xs font-bold uppercase tracking-wider"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 bg-white">
            {loading ? (
              <MastiSkeleton rows={5} />
            ) : error ? (
              <tr>
                <td
                  colSpan={5}
                  className="py-10 text-center text-sm text-red-400"
                >
                  {error}
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-14 text-center">
                  <div className="flex flex-col items-center gap-2 text-gray-400">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-10 w-10 opacity-40"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="text-sm font-medium">
                      No requests found
                    </span>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row, index) => {
                const isPending = row.status === "pending";
                const name = row.host?.nickName || "—";
                const phone = row.host?.phone || "—";
                const avatar = row.host?.selected_Avatar?.avatarUrl || null;

                return (
                  <tr
                    key={row._id}
                    className="border-b border-gray-100 text-center text-sm text-gray-700 transition-colors hover:bg-pink-50/40"
                  >
                    <td className="border px-4 py-4 font-medium text-gray-500">
                      {/* {(currentPage - 1) * limit + index + 1} */}
                      {row.applicationSerialNumber}
                    </td>

                    <td className="border px-4 py-4">
                      <div className="flex items-center justify-center gap-3">
                        <div className="relative shrink-0">
                          <img
                            src={
                              avatar ||
                              `https://api.dicebear.com/7.x/thumbs/svg?seed=${name}`
                            }
                            alt={name}
                            className="h-9 w-9 rounded-full border-2 border-pink-100 object-cover"
                          />
                          {row.isLive && (
                            <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white bg-green-400" />
                          )}
                        </div>
                        <div className="text-left">
                          <p className="font-semibold leading-tight text-gray-800">
                            {name}
                          </p>
                          <p className="text-xs text-gray-400">+91 {phone}</p>
                        </div>
                      </div>
                    </td>

                    <td className="border px-4 py-4 text-gray-600">
                      {row.name || "—"}
                    </td>

                    <td className="border px-4 py-4 text-gray-600">
                      {row?.category?.name || "—"}
                    </td>

                    <td className="border px-4 py-4 text-gray-600">
                      {row.language || "—"}
                    </td>

                    <td className="border px-4 py-4">
                      <TypeBadge type={row.type} />
                    </td>

                    <td className="border px-4 py-4">
                      {isPending && canEdit ? (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() =>
                              setPopup({ row, actionType: "approved" })
                            }
                            className="rounded-lg border border-green-200 p-1.5 text-green-500 transition-colors hover:bg-green-50"
                          >
                            <TbSquareRoundedCheck size={22} />
                          </button>

                          <button
                            onClick={() =>
                              setPopup({ row, actionType: "rejected" })
                            }
                            className="rounded-lg border border-red-200 p-1.5 text-red-400 transition-colors hover:bg-red-50"
                          >
                            <TbSquareRoundedX size={22} />
                          </button>
                        </div>
                      ) : statusFilter === "Live" ? (
                        <button
                          onClick={() => makeMastiOffline(row._id)}
                          className="rounded-lg bg-red-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-600"
                        >
                          Make Offline
                        </button>
                      ) : (
                        <StatusBadge status={row.status} />
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
        showFirstLast={true}
      />

      <ModalWrapper isOpen={!!popup} onClose={handleClose}>
        {popup && (
          <ActionModal
            request={popup.row}
            actionType={popup.actionType}
            onClose={handleClose}
            onSuccess={onActionSuccess}
          />
        )}
      </ModalWrapper>
    </>
  );
};

export default MastiTable;
